/**
 * NotificationSettingsTab - Admin panel for notification/email configuration
 * Configures owner email recipients and other notification settings
 */
import { useState, useEffect } from 'react';
import { Bell, Mail, Save, Send, CheckCircle, AlertCircle, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import api from '../../../utils/apiClient';

export default function NotificationSettingsTab({ authHeaders }) {
  const [settings, setSettings] = useState({
    owner_email_recipient: '',
    owner_email_enabled: true,
    owner_email_time: '22:00' // 10 PM Fiji time
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testSending, setTestSending] = useState(false);
  const [lastEmailLog, setLastEmailLog] = useState(null);

  useEffect(() => {
    fetchSettings();
    fetchLastEmailLog();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/admin/settings');
      setSettings({
        owner_email_recipient: res.data.owner_email_recipient || 'bulaccinocafe@gmail.com',
        owner_email_enabled: res.data.owner_email_enabled !== false,
        owner_email_time: res.data.owner_email_time || '22:00'
      });
    } catch (err) {
      console.error('Failed to fetch notification settings:', err);
      toast.error('Failed to load settings');
    }
    setLoading(false);
  };

  const fetchLastEmailLog = async () => {
    try {
      const res = await api.get('/api/orders/owner/email-log');
      if (res.data?.last_email) {
        setLastEmailLog(res.data.last_email);
      }
    } catch (err) {
      // Non-critical, ignore
    }
  };

  const saveSettings = async () => {
    // Validate email format
    const emails = settings.owner_email_recipient.split(',').map(e => e.trim()).filter(Boolean);
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const invalidEmails = emails.filter(e => !emailRegex.test(e));
    
    if (invalidEmails.length > 0) {
      toast.error(`Invalid email address: ${invalidEmails.join(', ')}`);
      return;
    }

    setSaving(true);
    try {
      await api.put('/api/admin/settings', {
        owner_email_recipient: settings.owner_email_recipient,
        owner_email_enabled: settings.owner_email_enabled,
        owner_email_time: settings.owner_email_time
      });
      toast.success('Notification settings saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save settings');
    }
    setSaving(false);
  };

  const sendTestEmail = async () => {
    setTestSending(true);
    try {
      const res = await api.post('/api/orders/owner/send-daily-email');
      if (res.data.success) {
        toast.success(`Test email sent! ${res.data.order_count} orders, $${res.data.total_value?.toFixed(2)} value`);
        fetchLastEmailLog();
      } else {
        toast.error(res.data.error || 'Failed to send test email');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send test email');
    }
    setTestSending(false);
  };

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="notification-settings-tab">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
            <Bell className="w-6 h-6 text-purple-600 dark:text-purple-400" />
          </div>
          <div>
            <h2 className="text-2xl font-heading text-foreground">Notification Settings</h2>
            <p className="text-muted-foreground text-sm">Configure email notifications and alerts</p>
          </div>
        </div>
        <Button
          onClick={saveSettings}
          disabled={saving}
          className="rounded-full"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      {/* Owner Daily Email Section */}
      <div className="bg-card rounded-2xl border border-border p-6 space-y-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
            <Mail className="w-5 h-5 text-amber-600 dark:text-amber-400" />
          </div>
          <div>
            <h3 className="font-heading text-lg text-foreground">Owner Order Daily Summary</h3>
            <p className="text-sm text-muted-foreground">
              Daily email summary of all owner orders at 10 PM Fiji time
            </p>
          </div>
        </div>

        <div className="space-y-4">
          {/* Enable/Disable Toggle */}
          <label className="flex items-center gap-3 p-4 bg-muted/30 rounded-xl cursor-pointer">
            <input
              type="checkbox"
              checked={settings.owner_email_enabled}
              onChange={(e) => updateSetting('owner_email_enabled', e.target.checked)}
              className="w-5 h-5 rounded border-gray-300"
            />
            <div>
              <div className="font-medium text-foreground">Enable Daily Summary Email</div>
              <div className="text-xs text-muted-foreground">
                Send automatic email summary every day at 10 PM Fiji time
              </div>
            </div>
          </label>

          {/* Email Recipients */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Email Recipients
            </label>
            <Input
              type="text"
              value={settings.owner_email_recipient}
              onChange={(e) => updateSetting('owner_email_recipient', e.target.value)}
              placeholder="owner@example.com, manager@example.com"
              className="bg-background"
              data-testid="owner-email-input"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Enter one or multiple email addresses separated by commas
            </p>
          </div>

          {/* Test Email Button */}
          <div className="flex items-center gap-4 pt-2">
            <Button
              onClick={sendTestEmail}
              disabled={testSending || !settings.owner_email_enabled}
              variant="outline"
              className="rounded-full"
              data-testid="send-test-email-btn"
            >
              <Send className="w-4 h-4 mr-2" />
              {testSending ? 'Sending...' : 'Send Test Email Now'}
            </Button>
            
            {/* Last Email Status */}
            {lastEmailLog && (
              <div className="flex items-center gap-2 text-sm">
                {lastEmailLog.success ? (
                  <CheckCircle className="w-4 h-4 text-green-500" />
                ) : (
                  <AlertCircle className="w-4 h-4 text-red-500" />
                )}
                <span className={lastEmailLog.success ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>
                  Last sent: {new Date(lastEmailLog.sent_at).toLocaleString()}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Info Box */}
        <div className="p-4 bg-blue-50 dark:bg-blue-950/30 rounded-xl border border-blue-200 dark:border-blue-800">
          <div className="text-sm text-blue-700 dark:text-blue-300">
            <strong>How it works:</strong>
            <ul className="list-disc list-inside mt-2 space-y-1">
              <li>Email is automatically sent daily at 10:00 PM Fiji time (FJT)</li>
              <li>Summary includes all owner orders placed that day</li>
              <li>Shows item details and total &ldquo;would have cost&rdquo; value</li>
              <li>Owner orders are marked in the POS and excluded from sales reports</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Schedule Info */}
      <div className="bg-gradient-to-r from-purple-500/10 to-indigo-500/10 rounded-2xl border border-purple-200 dark:border-purple-800 p-6">
        <div className="flex items-center gap-3">
          <Clock className="w-5 h-5 text-purple-500" />
          <div>
            <h4 className="font-medium text-foreground">Scheduled Email Time</h4>
            <p className="text-sm text-muted-foreground">
              Emails are sent at <strong>10:00 PM Fiji Time (FJT)</strong> every day.
              The system checks hourly and sends when it&apos;s 10 PM in Fiji.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
